import { Script } from "gatsby"
import { useState } from "react"

import { Box, SvgIcon as MUISvgIcon, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import useEmblaCarousel from "embla-carousel-react"

import type { FC, ResponsiveImageType, iconKeys } from ".."
import { Container, Grid, IconButton, Link, ResponsiveImage, SvgIcon, Tag } from ".."
import ArticleShare, { type ArticleShareProps } from "../ArticleShare/ArticleShare"
import { Author } from "../AuthorPreview/AuthorPreview"
import { ArticleBodyContent } from "./styles"

type ArticleAuthor = {
  image?: ResponsiveImageType
  name: string
  description: string
  link: { uri: string }
  position?: string
}

type ArticleTag = {
  title: string
  icon?: string | null
  uri?: string
}

type ArticleBodyProps = {
  description: string
  author?: Author
  tags?: ArticleTag[]
  shareLinks?: ArticleShareProps
  layout?: "article" | "guide"
  nextItem?: {
    title: string
    image?: ResponsiveImageType
    uri: string
  }
  isGeoPage?: boolean
  isExpandable?: boolean
}

const ArticleBody: FC<ArticleBodyProps> = ({
  description,
  author,
  tags,
  shareLinks,
  layout = "article",
  nextItem,
  isGeoPage = false,
  isExpandable = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [emblaRef] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    breakpoints: {
      "(min-width: 990px)": {
        active: false,
      },
    },
  })

  // Toggle expanded state
  const toggleReadMore = () => {
    setIsExpanded((prev) => !prev)
  }

  const getDisplayContent = () => {
    if (!isExpandable) return description

    // If expanded, show full description
    if (isExpanded) {
      return description
    }

    // If description contains h2 tags, truncate at first h2
    if (description?.includes("<h2")) {
      const truncatedAtH2 = description.substring(0, description.indexOf("<h2"))
      const words = truncatedAtH2.split(" ")
      return words.slice(0, -3).join(" ") + "..."
    }

    // If description is long, truncate at 50 chars
    if (description?.length > 50) {
      return description.substring(0, 50) + "..."
    }

    return description
  }

  const displayContent = getDisplayContent()
  const shouldShowReadMore = description?.length > 50 || description?.includes("<h2")

  return (
    <Container>
      <Grid>
        <Box
          gridColumn={{ xs: "1 / -1", lg: "2 / -2", xl: "3 / -3" }}
          py={isGeoPage ? 0 : { xs: 20, md: 45 }}
        >
          {isExpandable ? (
            <>
              {/* Visible content - truncated for user experience */}
              <ArticleBodyContent content={displayContent} sx={{ display: "block" }} />

              {/* Hidden content - full text for SEO crawlers
                  This ensures search engines can index the complete content
                  while maintaining a clean user experience with truncated text.
                  The content is hidden using CSS but remains in the DOM for crawlers. */}
              <ArticleBodyContent
                content={description}
                sx={{
                  position: "absolute",
                  opacity: 0,
                  height: 0,
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              />
            </>
          ) : (
            <ArticleBodyContent content={description} />
          )}
          {isExpandable && shouldShowReadMore && (
            <Link onClick={toggleReadMore} variant="link.xs">
              {isExpanded ? "Read Less" : "Read More"}
            </Link>
          )}

          {layout === "guide" && (
            <Stack direction="row" className="a2a_kit" mt={{ xs: 15, md: 30 }} py={3}>
              <Stack direction="row" alignItems="center" gap={3} position="relative">
                <SvgIcon variant="share" size={16} />
                <Typography variant="link.xs" fontWeight={500}>
                  Share This Article
                </Typography>
                <Link
                  type="overlay"
                  variant="link.xs"
                  target={undefined}
                  rel={undefined}
                  href="https://www.addtoany.com/share"
                  className="a2a_dd"
                  {...(shareLinks?.title && {
                    "data-a2a-title": `Check out this guide: ${shareLinks?.title}`,
                  })}
                  sx={{
                    opacity: 0,
                  }}
                />
              </Stack>
              <Script async src="https://static.addtoany.com/menu/page.js" />
            </Stack>
          )}
        </Box>
        {layout === "guide" && nextItem && (
          <>
            <Box gridColumn={{ xs: "1 / -1", lg: "2 / -2" }} mb={{ xs: 20, md: 45 }}>
              <Stack
                direction="row"
                columnGap={{ xs: 5, md: 15 }}
                alignItems="center"
                position="relative"
                sx={(theme) => ({
                  "borderRadius": 1.5,
                  "border": `1px solid ${theme.palette["neutral.200"].main}`,
                  "p": { xs: 3, md: 10 },
                  "transition": theme.transitions.create(["box-shadow"]),

                  "&:hover": {
                    boxShadow: `1px 1px 10px 0px ${alpha(theme.palette["neutral.900"].main, 0.15)}`,

                    img: {
                      transform: "scale(1.05)",
                    },
                  },
                })}
              >
                <Link
                  type="overlay"
                  href={nextItem.uri}
                  aria-label={`Go to next travel guide: ${nextItem.title}`}
                />
                <Box
                  borderRadius={1}
                  position="relative"
                  overflow="hidden"
                  sx={{ width: { xs: 120, md: 180 }, aspectRatio: "1" }}
                >
                  {nextItem.image && (
                    <ResponsiveImage
                      image={nextItem.image}
                      sx={(theme) => ({
                        transition: theme.transitions.create("transform", {
                          duration: theme.transitions.duration.standard * 1.5,
                        }),
                      })}
                    />
                  )}
                </Box>

                <Stack gap={5}>
                  <Typography variant="link.xs">Next Travel Guide</Typography>
                  <Typography variant="heading.lg">{nextItem.title}</Typography>
                </Stack>

                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={(theme) => ({
                    bgcolor: theme.palette["primary.800"].main,
                    borderRadius: 9999,
                    display: { xs: "none", md: "flex" },
                    height: 62,
                    width: 62,
                    ml: "auto",
                    mr: 10,
                  })}
                >
                  <SvgIcon
                    variant="arrowOutward"
                    size={16}
                    sx={(theme) => ({
                      fill: theme.palette["neutral.100"].main,
                    })}
                  />
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </Grid>

      {layout === "article" && (
        <>
          <Box ref={emblaRef} overflow="hidden">
            <ArticleTags tags={tags} />
          </Box>
          {author && (
            <ArticleAuthorSection
              name={author.title}
              description={author.description as string}
              link={author.link as any}
              image={author.photo}
              position={author.position}
            />
          )}
          <ArticleShare {...shareLinks} />
        </>
      )}
    </Container>
  )
}

export default ArticleBody

/** A sub-component for the article tags */
const ArticleTags: FC<Pick<ArticleBodyProps, "tags">> = ({ tags }) => (
  <Stack
    display={{ xs: "grid", md: "flex" }}
    flexDirection="row"
    flexWrap="wrap"
    rowGap={3}
    gridAutoFlow="column"
    gridAutoColumns="max-content"
    alignItems="center"
    columnGap={2}
    px={{ xs: 0, md: 15 }}
    py={{ xs: 12, md: 15 }}
    justifyContent={{ xs: "flex-start", md: "center" }}
    borderTop={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
  >
    <Typography variant="body.sm" sx={{ paddingRight: 3 }}>
      Related Topics
    </Typography>
    {tags?.map((tag) => (
      <Tag
        key={tag?.title}
        uri={tag?.uri}
        color="white"
        label={tag?.title}
        icon={tag?.icon as iconKeys}
        hasBorder
      />
    ))}
  </Stack>
)

/** A sub-component for the article author section */
const ArticleAuthorSection: FC<ArticleAuthor> = ({ name, description, link, image, position }) => {
  const imageWithAlt: ResponsiveImageType = {
    ...image,
    variants:
      image?.variants.map((img) => ({
        ...img,
        alt: name,
      })) ?? [],
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      px={{ xs: 5, md: 15 }}
      padding={(theme) => ({ xs: theme.spacing(3, 3, 9.5, 5), md: theme.spacing(5, 15) })}
      mb={10}
      gap={{ xs: 9.5, md: 30 }}
      sx={(theme) => ({
        minHeight: "450px",
        background: theme.palette["tertiary.300"].main,
        borderRadius: theme.spacing(1.5),
        [theme.breakpoints.down("md")]: {
          flexDirection: "column",
          minHeight: "100%",
        },
      })}
    >
      <Box
        position="relative"
        display="flex"
        justifyContent={{ xs: "flex-end", md: "center" }}
        width={{ xs: "100%", md: "auto" }}
        p={{ xs: 0, md: 5 }}
      >
        {imageWithAlt ? (
          <ResponsiveImage
            image={imageWithAlt}
            borderRadius={1.5}
            sx={{
              width: "220px",
              height: "220px",
            }}
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            bgcolor={(theme) => theme.palette["secondary.300"].main}
            borderRadius={1.5}
            sx={{ width: 220, height: 220 }}
          >
            <ArticleDefaultPhoto />
          </Stack>
        )}
        <Box
          position="absolute"
          display="flex"
          right={{ xs: 15, md: -25 }}
          bottom={{ xs: 20, md: -25 }}
          p={5}
          sx={(theme) => ({
            borderRadius: 9999,
            cursor: "pointer",
            [theme.breakpoints.down("md")]: {
              display: "none",
            },
          })}
        >
          <IconButton
            href={link?.uri}
            variant="quaternary"
            aria-label={`Read more about ${name}`}
            icon={
              <SvgIcon
                variant="arrowOutward"
                size={18}
                sx={{
                  fill: (theme) => theme.palette["neutral.100"].main,
                  transform: "rotate(45deg)",
                }}
              />
            }
          />
        </Box>
      </Box>
      <Stack direction="column" gap={5}>
        <ArticleAuthorName name={name} />
        {position && <ArticleAuthorPosition position={position} />}
        <ArticleAuthorDescription description={description} />
        <ArticleAuthorLink uri={link?.uri} />
      </Stack>
    </Box>
  )
}

/** A sub-component for the article author title */
const ArticleAuthorName: FC<{ name: string }> = ({ name }) => (
  <Box>
    <Typography variant="heading.xl" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
      {name}
    </Typography>
  </Box>
)

/** A sub-component for the article author position */
const ArticleAuthorPosition: FC<{ position: string }> = ({ position }) => (
  <Box>
    <Typography variant="body.sm" sx={(theme) => ({ color: theme.palette["neutral.700"].main })}>
      Goway - {position}
    </Typography>
  </Box>
)

/** A sub-component for the article author description */
const ArticleAuthorDescription: FC<{ description: string }> = ({ description }) => (
  <Box>
    <Typography
      variant="body.sm"
      sx={(theme) => ({ color: theme.palette["neutral.700"].main })}
      dangerouslySetInnerHTML={{ __html: description }}
    />
  </Box>
)

/** A sub-component for the article author link */
const ArticleAuthorLink: FC<{ uri: string }> = ({ uri }) => (
  <Box mt={5}>
    <Link href={uri} variant="link.xs">
      Read More
    </Link>
  </Box>
)

const ArticleDefaultPhoto: FC = () => (
  <MUISvgIcon viewBox="0 0 38 38" sx={{ width: 128, height: 128 }}>
    <path
      d="M34.0415 7.91667C34.9155 7.91667 35.6248 7.20733 35.6248 6.33333C35.6248 5.45933 34.9155 4.75 34.0415 4.75C33.1675 4.75 32.4582 5.45933 32.4582 6.33333C32.4582 7.20733 33.1675 7.91667 34.0415 7.91667ZM30.0832 4.75C31.3926 4.75 32.4582 3.68442 32.4582 2.375C32.4582 1.06558 31.3926 0 30.0832 0C28.7738 0 27.7082 1.06558 27.7082 2.375C27.7082 3.68442 28.7738 4.75 30.0832 4.75ZM30.0832 1.58333C30.5202 1.58333 30.8748 1.938 30.8748 2.375C30.8748 2.812 30.5202 3.16667 30.0832 3.16667C29.6462 3.16667 29.2915 2.812 29.2915 2.375C29.2915 1.938 29.6462 1.58333 30.0832 1.58333ZM13.3616 26.9563C13.9474 26.9167 14.5475 26.923 15.146 26.9847C15.5403 28.7676 17.1204 30.0833 18.9998 30.0833H23.7498C25.9317 30.0833 27.7082 28.3068 27.7082 26.125V21.375C27.7082 19.1932 25.9317 17.4167 23.7498 17.4167H4.74984C2.568 17.4167 0.791504 19.1932 0.791504 21.375V26.125C0.791504 28.3068 2.568 30.0833 4.74984 30.0833H9.49984C11.3998 30.0833 12.9816 28.7581 13.3616 26.9563ZM11.8653 26.2073C11.8194 27.493 10.7808 28.5 9.49984 28.5H4.74984C3.44042 28.5 2.37484 27.4344 2.37484 26.125V21.375C2.37484 20.0656 3.44042 19 4.74984 19H23.7498C25.0593 19 26.1248 20.0656 26.1248 21.375V26.125C26.1248 27.4344 25.0593 28.5 23.7498 28.5H18.9998C17.7363 28.5 16.7008 27.5073 16.6391 26.239C16.6201 25.8638 16.3414 25.5534 15.9709 25.4948C15.3423 25.3967 14.7138 25.3476 14.0915 25.3476C13.5738 25.3476 13.0576 25.3824 12.5509 25.4505C12.1678 25.5027 11.878 25.821 11.8653 26.2073ZM7.9165 20.5833H6.33317C5.02375 20.5833 3.95817 21.6489 3.95817 22.9583V24.5417C3.95817 24.9787 4.31284 25.3333 4.74984 25.3333C5.18684 25.3333 5.5415 24.9787 5.5415 24.5417V22.9583C5.5415 22.5213 5.89617 22.1667 6.33317 22.1667H7.9165C8.3535 22.1667 8.70817 21.812 8.70817 21.375C8.70817 20.938 8.3535 20.5833 7.9165 20.5833ZM37.0894 9.87525C36.9453 9.6425 36.6904 9.5 36.4165 9.5H33.2498C32.9759 9.5 32.721 9.64408 32.5769 9.87683C32.4328 10.1096 32.4186 10.4009 32.5421 10.6463L34.0415 13.6452V19.7917C34.0415 28.0852 27.2933 34.8333 18.9998 34.8333H18.0625C17.7348 33.9134 16.8639 33.25 15.8332 33.25H14.2498C12.9404 33.25 11.8748 34.3156 11.8748 35.625C11.8748 36.9344 12.9404 38 14.2498 38H15.8332C16.8639 38 17.7348 37.3366 18.0625 36.4167H18.9998C28.1673 36.4167 35.6248 28.9592 35.6248 19.7917V13.6436L37.1243 10.6448C37.2478 10.4009 37.2335 10.108 37.0894 9.87525ZM15.8332 36.4167H14.2498C13.8128 36.4167 13.4582 36.062 13.4582 35.625C13.4582 35.188 13.8128 34.8333 14.2498 34.8333H15.8332C16.2702 34.8333 16.6248 35.188 16.6248 35.625C16.6248 36.062 16.2702 36.4167 15.8332 36.4167Z"
      fill="#31456B"
    />
  </MUISvgIcon>
)
